import { useCallback, useEffect } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'

import { useTranslation } from '../i18n.js'
import { rxSourceLabel, rxTypeLabel } from '../status-labels.js'
import type { PrescriptionCopyData } from '../api/reads.js'

/**
 * The prescription copy a customer is given. §6.3 `RXW-003`, Appendix L, §8.1.
 *
 * `RXW-003` `MUST`: "generate a printable / PDF prescription document in the tenant's country
 * format, with practice details, prescriber, expiry, and a legend." Nothing built it, and it is the
 * first thing a customer asks for when they want to buy glasses somewhere else.
 *
 * **The legend is the clause that makes the sheet worth printing.** A customer holding
 * `OD -2.25 / -0.75 x 90 Add +2.25` cannot act on it, and the point of the document is that they
 * can take it to another dispenser. The server decides which abbreviations appear, because it knows
 * which fields the record has; this renders them and translates the meanings by term.
 *
 * **The powers arrive as signed strings and are printed, not parsed.** `RX-03` requires the sign
 * always, and this sheet leaves the shop: another dispenser reads it and grinds from it. Nothing on
 * this screen does arithmetic.
 *
 * **Latin and never mirrored**, like the job ticket. `LOC-005` keeps dioptric values and the
 * prescription grid outside the mirroring, and an Rx read right to left is one with the eyes
 * swapped. The sheet is `dir="ltr"` and the legend's meanings are the part that translates.
 *
 * A4 through the same named page the job ticket uses, because the default `@page` in this
 * stylesheet is the 58mm thermal roll (`PK-024`) and a prescription on a till roll is not a
 * document another practice will accept.
 */

export interface PrescriptionCopyProps {
  readonly copy: PrescriptionCopyData
  /**
   * The branch's own zone, for `LOC-004`.
   *
   * Every date on this sheet is already a calendar day resolved by the server (Appendix J), and
   * `formatDay` is what turns one into the form a shop writes: an ISO day on a document a customer
   * takes to another practice is a developer's date, not a date.
   */
  readonly timezone: string
  /** Injectable so a test can assert the print was asked for without jsdom opening a dialog. */
  readonly print?: () => void
}

export function PrescriptionCopy({
  copy,
  timezone,
  print,
}: PrescriptionCopyProps): JSX.Element {
  const { t, locale } = useTranslation()
  const day = (value: string): string => formatDay(value, timezone, locale)

  /**
   * The same body class the job ticket uses, for the same measured reason: a named page forces a
   * break wherever the value changes, and the root box uses the default `@page`, which here is the
   * thermal roll. Without this the first sheet of every printed prescription is blank 58mm paper.
   */
  useEffect(() => {
    const body = globalThis.document?.body
    body?.classList.add('lm-printing-ticket')
    return () => {
      body?.classList.remove('lm-printing-ticket')
    }
  }, [])

  const onPrint = useCallback(() => {
    if (print) {
      print()
      return
    }
    // Guarded because jsdom has no `print` and a test that opened one would hang.
    if (typeof window !== 'undefined' && typeof window.print === 'function') window.print()
  }, [print])

  return (
    <section className="lm-screen" aria-labelledby="rx-copy-heading">
      <div className="lm-screen__actions lm-no-print">
        <button type="button" className="lm-button lm-button--primary" onClick={onPrint}>
          {t('rxCopy.print')}
        </button>
      </div>

      <article className="lm-job-ticket__sheet" dir="ltr">
        <h1 id="rx-copy-heading" className="lm-job-ticket__blockHeading">
          {t('rxCopy.heading')}
        </h1>

        <p className="lm-job-ticket__shop">{copy.practice.name}</p>
        {copy.practice.addressLines.map((line) => (
          <p key={line} className="lm-rx-copy__address">
            {line}
          </p>
        ))}

        <section className="lm-job-ticket__block">
          <dl className="lm-job-ticket__facts">
            <dt>{t('rxCopy.customer')}</dt>
            <dd>{copy.customer.name}</dd>
            <dt>{t('rxCopy.dateOfBirth')}</dt>
            <dd>
              {copy.customer.dateOfBirth === undefined
                ? t('rxCopy.notGiven')
                : day(copy.customer.dateOfBirth)}
            </dd>
            <dt>{t('rxCopy.type')}</dt>
            <dd>{rxTypeLabel(t, copy.rxType)}</dd>
            <dt>{t('rxCopy.issued')}</dt>
            <dd>{day(copy.issueDate)}</dd>
            {copy.sightTestDate === undefined ? null : (
              <>
                <dt>{t('rxCopy.tested')}</dt>
                <dd>{day(copy.sightTestDate)}</dd>
              </>
            )}
            <dt>{t('rxCopy.expires')}</dt>
            <dd>
              {/* `RXW-004`: three states, not a date with a blank for two of them. "No expiry
                  recorded" and "expired" are different sentences, and a sheet that printed a blank
                  for both would be read as the first by another dispenser. */}
              {copy.expiry.kind === 'none'
                ? t('rxCopy.noExpiry')
                : copy.expiry.kind === 'expired'
                  ? t('rxCopy.expiredOn', { date: day(copy.expiry.on) })
                  : day(copy.expiry.on)}
            </dd>
            <dt>{t('rxCopy.version')}</dt>
            <dd>{copy.rxVersion}</dd>
          </dl>
        </section>

        <section className="lm-job-ticket__block">
          <table className="lm-job-ticket__rx">
            <thead>
              <tr>
                <th scope="col">{t('rxCopy.eye')}</th>
                <th scope="col">{t('rxCopy.sphere')}</th>
                <th scope="col">{t('rxCopy.cylinder')}</th>
                <th scope="col">{t('rxCopy.axis')}</th>
                <th scope="col">{t('rxCopy.add')}</th>
                <th scope="col">{t('rxCopy.pd')}</th>
                <th scope="col">{t('rxCopy.segHeight')}</th>
              </tr>
            </thead>
            <tbody>
              {copy.eyes.map((eye) => (
                <tr key={eye.eye}>
                  <th scope="row">{eye.eye.toUpperCase()}</th>
                  {/* Printed, never parsed: the server signed and rounded these. */}
                  <td>{eye.sphere}</td>
                  <td>{eye.cylinder}</td>
                  <td>{eye.axis ?? ''}</td>
                  <td>{eye.addPower ?? ''}</td>
                  <td>{eye.monocularPdMm ?? ''}</td>
                  <td>{eye.segmentHeightMm ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {copy.eyes.map((eye) =>
            eye.prismHorizontal === undefined && eye.prismVertical === undefined ? null : (
              <p key={`prism-${eye.eye}`} className="lm-job-ticket__prism">
                {/* A prism with no base is the wrong direction, so the base travels with it. */}
                {eye.eye.toUpperCase()}
                {eye.prismHorizontal === undefined
                  ? ''
                  : ` ${eye.prismHorizontal} ${eye.prismBaseHorizontal ?? ''}`}
                {eye.prismVertical === undefined
                  ? ''
                  : ` ${eye.prismVertical} ${eye.prismBaseVertical ?? ''}`}
              </p>
            ),
          )}
        </section>

        <section className="lm-job-ticket__block">
          <h2 className="lm-job-ticket__blockHeading">{t('rxCopy.prescriber')}</h2>
          <dl className="lm-job-ticket__facts">
            <dt>{t('rxCopy.prescriberName')}</dt>
            <dd>{copy.prescriber?.name ?? t('rxCopy.notGiven')}</dd>
            <dt>{t('rxCopy.registration')}</dt>
            <dd>{copy.prescriber?.registration ?? t('rxCopy.notGiven')}</dd>
            {copy.prescriber?.practice === undefined ? null : (
              <>
                <dt>{t('rxCopy.prescriberPractice')}</dt>
                <dd>{copy.prescriber.practice}</dd>
              </>
            )}
            <dt>{t('rxCopy.source')}</dt>
            {/* `RXW-005`: an outside prescription is "a supported and clearly-flagged source", and
                the flag belongs on the document another dispenser reads. */}
            <dd>{rxSourceLabel(t, copy.source)}</dd>
          </dl>
        </section>

        <section className="lm-job-ticket__block">
          <h2 className="lm-job-ticket__blockHeading">{t('rxCopy.legend')}</h2>
          {/* Appendix L's "a legend explaining the abbreviations". Only the terms this sheet used:
              a legend listing prism on a single-vision sheet is one people stop reading. */}
          <dl className="lm-rx-copy__legend">
            {copy.legend.map((entry) => (
              <div key={entry.term} className="lm-rx-copy__legendRow">
                <dt>{entry.term}</dt>
                <dd>{entry.meaning}</dd>
              </div>
            ))}
          </dl>
        </section>

        {copy.absent.length === 0 ? null : (
          <section className="lm-job-ticket__block lm-job-ticket__block--warn">
            <h2 className="lm-job-ticket__blockHeading">{t('rxCopy.notSupplied')}</h2>
            <ul className="lm-job-ticket__absent">
              {copy.absent.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </section>
  )
}
